import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, orderBy, limit, startAfter, getDocs, doc, updateDoc, deleteDoc, QueryDocumentSnapshot, DocumentData, where } from 'firebase/firestore';
import { ArrowLeft, Edit2, Trash2, Save, X, ChevronLeft, ChevronRight, ArrowUpDown, Search } from 'lucide-react';
import { Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Registration, Firm } from '../types';
import { format } from 'date-fns';

interface RegistrationWithId extends Registration {
  id: string;
}

type SortField = 'timestamp' | 'licensePlate' | 'countryCode' | 'busCompany' | 'userId';
type SortDirection = 'asc' | 'desc';

interface PaginationState {
  currentPage: number;
  firstDocs: QueryDocumentSnapshot<DocumentData>[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasNextPage: boolean;
}

export default function ManageRegistrations() {
  const { signOut } = useAuth();
  const [registrations, setRegistrations] = useState<RegistrationWithId[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<Partial<Registration>>({});
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    firstDocs: [],
    lastDoc: null,
    hasNextPage: false
  });
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'licensePlate' | 'busCompany'>('licensePlate');
  const [isSearching, setIsSearching] = useState(false);

  const ITEMS_PER_PAGE = 20;

  // Fetch firms for dropdown
  useEffect(() => {
    const fetchFirms = async () => {
      try {
        const firmsSnapshot = await getDocs(collection(db, 'firms'));
        const firmsData = firmsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as (Firm & { id: string })[];
        
        // Sort firms alphabetically by name
        firmsData.sort((a, b) => a.name.localeCompare(b.name));
        setFirms(firmsData);
      } catch (error) {
        console.error('Error fetching firms:', error);
      }
    };

    fetchFirms();
  }, []);

  // Build query based on search and sort parameters
  const buildQuery = useCallback((
    searchQuery: string = '',
    searchType: 'licensePlate' | 'busCompany' = 'licensePlate',
    sortBy: SortField = sortField,
    sortDir: SortDirection = sortDirection,
    cursor?: QueryDocumentSnapshot<DocumentData>,
    direction: 'next' | 'prev' = 'next'
  ) => {
    let q = collection(db, 'registrations');
    let constraints: any[] = [];

    // Add search constraints
    if (searchQuery.trim()) {
      const searchValue = searchQuery.trim();
      // For prefix search, we use startAt/endAt with the search term
      constraints.push(
        where(searchType, '>=', searchValue),
        where(searchType, '<=', searchValue + '\uf8ff')
      );
      // When searching, we need to order by the search field first
      constraints.push(orderBy(searchType));
      if (searchType !== sortBy) {
        constraints.push(orderBy(sortBy, sortDir));
      }
    } else {
      // Normal sorting when not searching
      constraints.push(orderBy(sortBy, sortDir));
    }

    // Add pagination cursor
    if (cursor) {
      if (direction === 'next') {
        constraints.push(startAfter(cursor));
      } else {
        // For previous page, we need to reverse the order and use startAfter
        // This is a workaround since Firestore doesn't have startBefore
        const reversedDir = sortDir === 'asc' ? 'desc' : 'asc';
        constraints = constraints.map(constraint => {
          if (constraint.type === 'orderBy' && constraint.field === sortBy) {
            return orderBy(sortBy, reversedDir);
          }
          return constraint;
        });
        constraints.push(startAfter(cursor));
      }
    }

    constraints.push(limit(ITEMS_PER_PAGE + 1)); // +1 to check if there's a next page

    return query(q, ...constraints);
  }, [sortField, sortDirection]);

  // Fetch registrations with proper pagination
  const fetchRegistrations = useCallback(async (
    page: number = 1,
    direction: 'next' | 'prev' | 'reset' = 'reset',
    searchQuery: string = searchTerm,
    searchType: 'licensePlate' | 'busCompany' = searchField
  ) => {
    setLoading(true);
    
    try {
      let cursor: QueryDocumentSnapshot<DocumentData> | undefined;
      
      if (direction === 'next' && pagination.lastDoc) {
        cursor = pagination.lastDoc;
      } else if (direction === 'prev' && page > 1) {
        // For previous page, use the first document of the current page
        const firstDocIndex = page - 2; // -2 because we want the doc before the previous page
        cursor = pagination.firstDocs[firstDocIndex];
      }

      const q = buildQuery(searchQuery, searchType, sortField, sortDirection, cursor, direction);
      const querySnapshot = await getDocs(q);
      
      let docs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as RegistrationWithId));

      // If we're going to previous page and reversed the order, reverse the results back
      if (direction === 'prev') {
        docs = docs.reverse();
      }

      // Check if there's a next page
      const hasMore = docs.length > ITEMS_PER_PAGE;
      if (hasMore) {
        docs = docs.slice(0, ITEMS_PER_PAGE); // Remove the extra item
      }

      // Update pagination state
      const newFirstDocs = [...pagination.firstDocs];
      if (direction === 'reset' || page === 1) {
        newFirstDocs.length = 0; // Clear array
      }
      
      if (docs.length > 0) {
        const firstDoc = querySnapshot.docs[0];
        const lastDoc = querySnapshot.docs[Math.min(docs.length - 1, querySnapshot.docs.length - 1)];
        
        // Store first document for this page
        if (direction === 'next' || direction === 'reset') {
          newFirstDocs[page - 1] = firstDoc;
        }

        setPagination({
          currentPage: page,
          firstDocs: newFirstDocs,
          lastDoc: hasMore ? lastDoc : null,
          hasNextPage: hasMore
        });
      } else {
        setPagination({
          currentPage: page,
          firstDocs: newFirstDocs,
          lastDoc: null,
          hasNextPage: false
        });
      }

      setRegistrations(docs);
      setIsSearching(!!searchQuery.trim());
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, sortField, sortDirection, searchTerm, searchField, pagination.lastDoc, pagination.firstDocs]);

  // Initial load and when sort changes
  useEffect(() => {
    setPagination({
      currentPage: 1,
      firstDocs: [],
      lastDoc: null,
      hasNextPage: false
    });
    fetchRegistrations(1, 'reset', searchTerm, searchField);
  }, [sortField, sortDirection]);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPagination({
        currentPage: 1,
        firstDocs: [],
        lastDoc: null,
        hasNextPage: false
      });
      fetchRegistrations(1, 'reset', searchTerm, searchField);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchField]);

  const handleNextPage = () => {
    if (pagination.hasNextPage) {
      const nextPage = pagination.currentPage + 1;
      fetchRegistrations(nextPage, 'next', searchTerm, searchField);
    }
  };

  const handlePrevPage = () => {
    if (pagination.currentPage > 1) {
      const prevPage = pagination.currentPage - 1;
      fetchRegistrations(prevPage, 'prev', searchTerm, searchField);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleEdit = (registration: RegistrationWithId) => {
    setEditingId(registration.id);
    setEditingData({
      licensePlate: registration.licensePlate,
      countryCode: registration.countryCode,
      busCompany: registration.busCompany,
      merknad: registration.merknad
    });
  };

  const handleSave = async (id: string) => {
    if (!editingData.licensePlate?.trim() || !editingData.busCompany?.trim()) {
      alert('Registreringsnummer og busselskap er påkrevd');
      return;
    }
    
    try {
      await updateDoc(doc(db, 'registrations', id), {
        licensePlate: editingData.licensePlate.trim(),
        countryCode: editingData.countryCode?.trim() || '',
        busCompany: editingData.busCompany.trim(),
        merknad: editingData.merknad?.trim() || ''
      });
      
      setRegistrations(registrations.map(reg => 
        reg.id === id ? { 
          ...reg, 
          licensePlate: editingData.licensePlate!.trim(),
          countryCode: editingData.countryCode?.trim() || '',
          busCompany: editingData.busCompany!.trim(),
          merknad: editingData.merknad?.trim() || ''
        } : reg
      ));
      
      setEditingId(null);
      setEditingData({});
    } catch (error) {
      console.error('Error updating registration:', error);
      alert('Feil ved oppdatering av registrering');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Er du sikker på at du vil slette denne registreringen?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'registrations', id));
      
      // Refresh current page after deletion
      fetchRegistrations(pagination.currentPage, 'reset', searchTerm, searchField);
    } catch (error) {
      console.error('Error deleting registration:', error);
      alert('Feil ved sletting av registrering');
    }
  };

  if (loading && registrations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link
                to="/"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors duration-150"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tilbake til dashboard
              </Link>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Administrer registreringer</h1>
            <button
              onClick={() => signOut()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Logg ut
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                <h2 className="text-lg font-medium text-gray-900">
                  Registreringer {isSearching && `(søker: "${searchTerm}")`}
                </h2>
                <div className="flex items-center space-x-4">
                  <Link
                    to="/import-registrations"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Importer
                  </Link>
                  <div className="flex items-center space-x-2">
                    <select
                      value={searchField}
                      onChange={(e) => setSearchField(e.target.value as 'licensePlate' | 'busCompany')}
                      className="rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="licensePlate">Reg.nr</option>
                      <option value="busCompany">Selskap</option>
                    </select>
                    <div className="relative">
                      <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder={`Søk etter ${searchField === 'licensePlate' ? 'registreringsnummer' : 'selskap'}...`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={pagination.currentPage === 1}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Forrige
                    </button>
                    <span className="text-sm text-gray-700">
                      Side {pagination.currentPage}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={!pagination.hasNextPage}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      Neste
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('timestamp')}
                    >
                      <div className="flex items-center">
                        Dato
                        <ArrowUpDown className="h-4 w-4 ml-1" />
                        {sortField === 'timestamp' && (
                          <span className="ml-1 text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('licensePlate')}
                    >
                      <div className="flex items-center">
                        Reg.nr
                        <ArrowUpDown className="h-4 w-4 ml-1" />
                        {sortField === 'licensePlate' && (
                          <span className="ml-1 text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('countryCode')}
                    >
                      <div className="flex items-center">
                        Land
                        <ArrowUpDown className="h-4 w-4 ml-1" />
                        {sortField === 'countryCode' && (
                          <span className="ml-1 text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('busCompany')}
                    >
                      <div className="flex items-center">
                        Selskap
                        <ArrowUpDown className="h-4 w-4 ml-1" />
                        {sortField === 'busCompany' && (
                          <span className="ml-1 text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Merknad
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('userId')}
                    >
                      <div className="flex items-center">
                        Bruker ID
                        <ArrowUpDown className="h-4 w-4 ml-1" />
                        {sortField === 'userId' && (
                          <span className="ml-1 text-blue-600">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handlinger
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {registrations.map((registration, index) => (
                    <tr key={registration.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(registration.timestamp.seconds * 1000), 'dd.MM.yyyy HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingId === registration.id ? (
                          <input
                            type="text"
                            value={editingData.licensePlate || ''}
                            onChange={(e) => setEditingData({...editingData, licensePlate: e.target.value})}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          />
                        ) : (
                          <div className="text-sm font-medium text-gray-900">{registration.licensePlate}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingId === registration.id ? (
                          <input
                            type="text"
                            value={editingData.countryCode || ''}
                            onChange={(e) => setEditingData({...editingData, countryCode: e.target.value})}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          />
                        ) : (
                          <div className="text-sm text-gray-900">{registration.countryCode}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingId === registration.id ? (
                          <select
                            value={editingData.busCompany || ''}
                            onChange={(e) => setEditingData({...editingData, busCompany: e.target.value})}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          >
                            <option value="">Velg selskap...</option>
                            {firms.map(firm => (
                              <option key={firm.id || firm.name} value={firm.name}>
                                {firm.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-sm text-gray-900">{registration.busCompany}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {editingId === registration.id ? (
                          <textarea
                            value={editingData.merknad || ''}
                            onChange={(e) => setEditingData({...editingData, merknad: e.target.value})}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                            rows={2}
                          />
                        ) : (
                          <div className="text-sm text-gray-900">{registration.merknad}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {registration.userId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {editingId === registration.id ? (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleSave(registration.id)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700"
                            >
                              <Save className="h-3 w-3 mr-1" />
                              Lagre
                            </button>
                            <button
                              onClick={handleCancel}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Avbryt
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(registration)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-600 hover:text-blue-900"
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Rediger
                            </button>
                            <button
                              onClick={() => handleDelete(registration.id)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Slett
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {registrations.length === 0 && !loading && (
                <div className="text-center py-12">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">
                      {searchTerm ? 'Ingen registreringer funnet' : 'Ingen registreringer'}
                    </p>
                    <p className="mt-2">
                      {searchTerm ? 'Prøv et annet søkeord.' : 'Det er ingen registreringer å vise.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {loading && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}